import { Injectable, OnDestroy, ViewContainerRef } from "@angular/core";
import { PopoverBuilderService } from "./popover-builder.service";
import { CellHoverTarget, PopoverControlData } from "./popover.models";
import { interval, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { HtmlElementAnimationService } from "@nms-ng2/app/shared/services/dom/html-element-animation.service";
import { MaxGraphEventBus } from "../setup/max-graph-event-bus";
import { CustomCell } from "../models/diagram.models";
import { CARD_OUTDATED_BG, CIRCUIT_GRAY, STATUS_MAJOR_COLOR } from "@nms-ng2/app/shared/constants/colors";

/**
 * Gerenciador de exibição dos popovers do diagrama.
 */
@Injectable({
    providedIn: "root"
})
export class PopoverVisibilityManagerService implements OnDestroy {

    private readonly popovers = new Map<string, PopoverControlData>();
    private readonly graphEventBusSubscriptions: Set<Subscription> = new Set();

    constructor(private readonly htmlElementAnimationService: HtmlElementAnimationService,
            private readonly popoverBuilder: PopoverBuilderService, private readonly maxGraphEventBus: MaxGraphEventBus) {
        const graphOnEnterSub = this.maxGraphEventBus.onHoverEnter$().subscribe((cell: CustomCell) => this.onEnter(cell.id));
        const graphOnLeaveSub = this.maxGraphEventBus.onHoverLeave$().subscribe((cell: CustomCell) => this.onLeave(cell.id));

        this.graphEventBusSubscriptions.add(graphOnEnterSub);
        this.graphEventBusSubscriptions.add(graphOnLeaveSub);
    }

    ngOnDestroy(): void {
        this.destroy();
        this.graphEventBusSubscriptions.forEach(sub => sub.unsubscribe());
    }

    /**
     * Controla a exibição do popover da célula.
     *
     * @param viewContainerRef - referência do container.
     * @param target - célula que recebe o hover.
     * @returns - void
     */
    show(viewContainerRef: ViewContainerRef, target: CellHoverTarget): void {
        const entry = this.popovers.get(target.id)
        if (entry) {
            this.resetHideSubscription(entry, target.id);
            return;
        }

        const componentRef = this.popoverBuilder.build(viewContainerRef, target);
        const subscription = componentRef.instance.pinned.subscribe(isFixed => target.isFixed = isFixed);
        const popover: PopoverControlData = {
            componentRef,
            cellTarget: target,
            isHovering: false,
            removingUnderway: false,
            hideSubscription: interval(500)
                .pipe(take(1))
                .subscribe(() => this.hide(target.id)),
            fixSubscription: subscription
        };

        this.popovers.set(target.id, popover);
        const element = componentRef.location.nativeElement as HTMLElement;

        element.addEventListener("mouseenter", () => this.onEnter(target.id));
        element.addEventListener("mouseleave", () => this.onLeave(target.id));
    }


    /**
     * Limpa todos os popovers.
     */
    destroy() {
        this.popovers.forEach((target, key) => {
            target.cellTarget.isFixed = false;
            this.hide(key);
        });
    }

    /**
     * Desatualiza todos os popovers
     */
    outdateAll (): void {
        this.popovers.forEach(card => {
            card.componentRef.instance.data.bgColor = CARD_OUTDATED_BG;
            card.componentRef.instance.data.iconExtra = "error";
            card.componentRef.instance.data.iconExtraColor = STATUS_MAJOR_COLOR;
            card.componentRef.instance.data.tipIconMessage = "nms.diagram-card.outdated.tip";
            card.componentRef.instance.data.separatorColor = CIRCUIT_GRAY;
        });
    }

    /**
     * Avalia se existem popovers fixados.
     */
    hasFixedPopovers(): boolean {
        return this.popovers.size > 0 && [... this.popovers.values()].some(popover => popover.componentRef.instance.isFixed);
    }

    private onEnter(targetId: string) {
        const entry = this.popovers.get(targetId);

        if (entry) {
            entry.isHovering = true
        }
    }

    private onLeave(targetId: string) {
        const entry = this.popovers.get(targetId);

        if (entry) {
            entry.isHovering = false;
            this.resetHideSubscription(entry, targetId)
        }
    }

    /**
     * Esconde o popover.
     *
     * @param id - id do target (célula).
     * @returns void
     */
    private hide(id: string): void {
        const entry = this.popovers.get(id);

        if (!this.isAbleToRemove(entry)) {
            return;
        }

        entry.removingUnderway = true;
        const element = entry.componentRef.location.nativeElement as HTMLElement;
        const onFinishCallback = () => {
            element.hidden = true;

            entry.hideSubscription?.unsubscribe();
            entry.fixSubscription?.unsubscribe();
            entry.componentRef.destroy();

            this.popovers.delete(id);
        };

        this.htmlElementAnimationService.opacityOut(element, 200, onFinishCallback)
    }

    /**
     * Um popover só pode ser removido se:
     *  - Existir
     *  - Não estiver com a remoção em andamento (animação)
     *  - Não estiver fixado
     *  - Não estiver com o mouse sob ele (hover)
     * @param entry - Popover
     * @returns - true se é possível remover, false caso contrário.
     */
    private isAbleToRemove(entry: PopoverControlData) {
        return entry && !entry.removingUnderway && !entry.cellTarget.isFixed && !entry.isHovering;
    }

    /**
     * Reseta a subscription caso o popover ou target receba hover novamente,
     * para que o popover não seja indevidamente fechado.
     *
     * @param entry - Popover
     * @param id - id do target (célula)
     */
    private resetHideSubscription(entry: PopoverControlData, id: string) {
        entry.hideSubscription?.unsubscribe();

        entry.hideSubscription = interval(500)
            .pipe(take(1))
            .subscribe(() => this.hide(id));
    }
}
